import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { DollarSign, Download, Building2, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function BillingPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  });

  const { data, isLoading } = useQuery({
    queryKey: ['billing', selectedMonth],
    queryFn: () => api.getBilling({ month: selectedMonth }),
  });

  const { data: unreviewedData } = useQuery({
    queryKey: ['work-logs-unreviewed', selectedMonth],
    queryFn: () => api.getWorkLogs({
      start_date: selectedMonth,
      end_date: new Date(new Date(selectedMonth).getFullYear(), new Date(selectedMonth).getMonth() + 1, 1).toISOString().split('T')[0],
      requires_review: true,
      limit: 100,
    }),
    enabled: isAdmin,
  });

  const unreviewedCount = (unreviewedData?.data ?? []).filter(wl => !wl.reviewed).length;

  const billing = data?.data ?? [];
  const totalAmount = billing.reduce((sum, b) => sum + parseFloat(String(b.total_amount)), 0);
  const totalBillableHours = billing.reduce((sum, b) => sum + parseFloat(String(b.billable_hours)), 0);
  const totalLogs = billing.reduce((sum, b) => sum + parseInt(String(b.total_logs)), 0);

  const handleExport = async () => {
    const token = localStorage.getItem('auth_token');
    const url = api.getExportBillingUrl({ month: selectedMonth });
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `billing-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  // Generate month options
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const label = d.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: val, label });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title tracking-tight font-headline">{t('billing.title')}</h1>
        <div className="flex items-center gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="input py-2 text-sm"
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            onClick={handleExport}
            className="btn bg-success-600 text-white hover:bg-success-700 flex items-center gap-2"
          >
            <Download size={18} />
            <span>{t('common.exportCsv')}</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-kpi">
              <div className="skeleton-value" />
              <div className="skeleton-label" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-warning-100 rounded-lg">
                <DollarSign className="text-warning-600" size={24} />
              </div>
              <div>
                <p className="text-surface-600 text-sm">{t('billing.totalBillable')}</p>
                <p className="text-2xl font-bold text-surface-900 tabular-nums font-display">{totalAmount.toLocaleString('he-IL', { maximumFractionDigits: 0 })} &#8362;</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success-100 rounded-lg">
                <Clock className="text-success-600" size={24} />
              </div>
              <div>
                <p className="text-surface-600 text-sm">{t('billing.billableHours')}</p>
                <p className="text-2xl font-bold text-surface-900 tabular-nums font-display">{totalBillableHours.toFixed(1)}</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-info-100 rounded-lg">
                <Building2 className="text-info-600" size={24} />
              </div>
              <div>
                <p className="text-surface-600 text-sm">{t('common.records')}</p>
                <p className="text-2xl font-bold text-surface-900 tabular-nums font-display">{totalLogs}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAdmin && unreviewedCount > 0 && (
        <div className="flex items-center gap-3 bg-warning-50 border border-warning-200 rounded-lg px-4 py-3">
          <AlertCircle size={20} className="text-warning-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            <span className="tabular-nums font-display">{unreviewedCount}</span>{' '}
            {t('workLogs.unreviewedRecords')}
          </p>
          <Link to="/work-logs" className="text-sm text-warning-700 underline hover:text-yellow-900 mr-auto">
            {t('common.viewAll')}
          </Link>
        </div>
      )}

      {/* Billing Table */}
      {isLoading ? (
        <div className="skeleton-card">
          <div className="h-48 bg-surface-200 rounded-lg animate-pulse" />
        </div>
      ) : billing.length > 0 ? (
        <div className="card overflow-hidden p-0">
          <div className="table-container">
          <table className="table-modern">
            <thead>
              <tr>
                <th className="text-right">{t('billing.org')}</th>
                <th className="text-right">{t('billing.billingModel')}</th>
                <th className="text-right">{t('billing.rate')}</th>
                <th className="text-right">{t('common.records')}</th>
                <th className="text-right">{t('billing.totalHours')}</th>
                <th className="text-right">{t('billing.billableHours')}</th>
                {isAdmin && <th className="text-right">{t('billing.retainer')}</th>}
                <th className="text-right">{t('billing.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {billing.map((b) => {
                const bHours = parseFloat(String(b.billable_hours));
                const retainerExceeded = b.billing_model === 'retainer' && b.retainer_hours && bHours > b.retainer_hours;
                return (
                  <tr key={b.organization_id}>
                    <td className="font-medium text-surface-900">{b.organization_name}</td>
                    <td>{b.billing_model === 'hourly' ? t('organization.hourly') : t('billing.retainer')}</td>
                    <td className="tabular-nums font-display">{b.hourly_rate} &#8362;</td>
                    <td className="tabular-nums font-display">{b.total_logs}</td>
                    <td className="tabular-nums font-display">{parseFloat(String(b.total_hours)).toFixed(1)}</td>
                    <td className="tabular-nums font-display">{bHours.toFixed(1)}</td>
                    {isAdmin && (
                      <td>
                        {b.billing_model === 'retainer' ? (
                          <span className={`tabular-nums font-display px-2 py-1 rounded text-xs font-medium ${retainerExceeded ? 'bg-danger-100 text-red-800' : 'bg-success-100 text-green-800'}`}>
                            {bHours.toFixed(1)} / {b.retainer_hours}
                          </span>
                        ) : '-'}
                      </td>
                    )}
                    <td className="font-bold text-surface-900 tabular-nums font-display">
                      {parseFloat(String(b.total_amount)).toLocaleString('he-IL', { maximumFractionDigits: 0 })} &#8362;
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state py-12">
            <DollarSign className="empty-state-icon" size={40} strokeWidth={1.25} />
            <p className="empty-state-text">{t('billing.noData')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
